import React from 'react';
import { type ButtonProps } from 'antd';
import CommonButton, { type ButtonSize, type CustomButtonProps } from 'components/common/button';

// Every difference between the two prop surfaces is a place a migrated call site could silently
// change behaviour, so each is spelled out rather than spread through.
export const toCommonProps = ({
  loading,
  danger,
  type,
  size,
  shape,
  target,
  ...rest
}: ButtonProps): CustomButtonProps => ({
  ...rest,
  // antd puts `target` on the anchor it renders from `href`. The wrapper has no `target`, so left in
  // rest it lands on the inner <button>, where it does nothing and the link opens in the same tab.
  // `isExternalLink` is how the wrapper says "real anchor, new tab".
  isExternalLink: target === '_blank' || undefined,
  // antd's `default` shape is the wrapper's unset; only circle and round are real variants.
  shape: shape === 'default' ? undefined : shape,
  // antd carries `danger` as a boolean alongside `type`; the wrapper folds it into one union. `text`
  // is the wrapper's `link`. `dashed` and `ghost` are not in the design system, so they degrade.
  type: danger ? 'danger' : type === 'text' ? 'link' : type === 'primary' || type === 'link' ? type : 'default',
  // antd's middle is the wrapper's unset. Passing it through would be an unknown size.
  size: size === 'middle' ? undefined : (size as ButtonSize | undefined),
  isLoading: typeof loading === 'object' ? true : Boolean(loading)
});

// `_blank` is the only target the wrapper can honour (toCommonProps folds it into `isExternalLink`); any
// other value would silently vanish, so the prop surface rejects it at compile time instead.
export type BridgedButtonProps = Omit<ButtonProps, 'target'> & { target?: '_blank' };

/**
 * Takes antd's Button props for call sites written against antd, and renders the common wrapper.
 * Moving those sites onto the wrapper's own props removes this adapter.
 *
 * The common wrapper forwards no ref, so the ref stops here; it is accepted so antd's Tooltip and
 * Dropdown can still hand one to their child without a React warning.
 */
export const BridgedButton = Object.assign(
  // oxlint-disable-next-line no-unused-vars -- forwardRef passes a ref the wrapper has nowhere to forward
  React.forwardRef<HTMLButtonElement | HTMLAnchorElement, BridgedButtonProps>(function BridgedButton(props, _ref) {
    return <CommonButton {...toCommonProps(props)} />;
  }),
  // antd Tooltip's disabled-button compat checks this static on the child's type (tooltip/index.js in
  // antd 5.6.4); hidden behind the bridge, a disabled button's tooltip stops firing on hover.
  { __ANT_BUTTON: true }
);

export default BridgedButton;
